import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Brain, Send, Plus, Trash2, MessageSquare, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import api, { streamChat } from '../../api';
import { AI } from '../../constants/testIds';

const SUGGESTIONS = {
  teacher: [
    "Create a JSS 2 Mathematics lesson plan on quadratic equations",
    "Generate 10 multiple choice questions on the topic of photosynthesis for SS1",
    "Write a 30-mark essay question for English Language, JSS3",
    "Suggest 5 teaching strategies for a mixed-ability class",
  ],
  school_admin: [
    "Summarize today's school attendance performance",
    "How can I improve student fee collection rates?",
    "Draft a circular for parents about upcoming exams",
    "What are best practices for managing student records?",
  ],
  school_owner: [
    "How can I improve student fee collection rates?",
    "Summarize this term's academic performance",
    "What features should I enable first as a new school?",
    "Draft a welcome message for new parents",
  ],
  master_admin: [
    "Show me platform growth trends for this quarter",
    "What schools are on trial and may need follow-up?",
    "How can I improve school onboarding experience?",
    "Summarize key metrics across all schools",
  ],
  student: [
    "Explain quadratic equations with examples",
    "Help me create a study plan for my upcoming exams",
    "Explain the causes of World War II in simple terms",
    "Give me 5 practice questions on photosynthesis",
  ],
  parent: [
    "How can I help my child improve in Mathematics?",
    "What are effective study habits for secondary school students?",
    "How do I communicate effectively with my child's teachers?",
    "Explain what a cumulative GPA means for my child",
  ],
};

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={AI.chatMessage}>
      {!isUser && (
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center flex-shrink-0 mt-1">
          <Brain className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser
          ? 'bg-[#516EFA] text-white rounded-br-sm'
          : 'bg-white border border-slate-100 text-slate-800 rounded-bl-sm shadow-sm ai-markdown'
      }`}>
        {isUser ? (msg.content || msg.text) : (
          <ReactMarkdown>{msg.content || msg.text || ''}</ReactMarkdown>
        )}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1 text-xs font-bold text-slate-600">
          U
        </div>
      )}
    </div>
  );
}

export default function AiAssistant() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const role = user?.role || 'teacher';
  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.teacher;

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const loadConversations = async () => {
    setLoadingConvs(true);
    try {
      const res = await api.get('/ai/conversations');
      setConversations(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoadingConvs(false);
    }
  };

  const loadMessages = async (convId) => {
    setActiveConvId(convId);
    setMessages([]);
    try {
      const res = await api.get(`/ai/conversations/${convId}/messages`);
      setMessages(res.data || []);
    } catch {
      toast.error('Failed to load messages');
    }
  };

  const startNewConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setInput('');
  };

  const handleDelete = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConvId === convId) startNewConversation();
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const sendMessage = async (text) => {
    const messageText = (text || input).trim();
    if (!messageText || sending) return;

    setInput('');
    setSending(true);
    setStreamingText('');

    const userMsg = { id: Date.now(), role: 'user', content: messageText };
    setMessages(prev => [...prev, userMsg]);

    let convId = activeConvId;

    try {
      const response = await streamChat(messageText, convId);
      if (!response.ok) throw new Error('Stream request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accText = '';
      let newConvId = convId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.error) throw new Error(data.error);
            if (data.token) {
              accText += data.token;
              setStreamingText(accText);
            }
            if (data.conversation_id && !newConvId) {
              newConvId = data.conversation_id;
              setActiveConvId(newConvId);
            }
            if (data.done) {
              setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: accText }]);
              setStreamingText('');
              // Refresh conversations list
              if (!convId && newConvId) {
                await loadConversations();
              }
            }
          } catch {/* skip malformed lines */}
        }
      }
    } catch (err) {
      toast.error('Failed to get AI response. Please try again.');
      setStreamingText('');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const roleLabel = {
    teacher: 'Teaching Assistant',
    school_admin: 'School Manager',
    school_owner: 'School Manager',
    master_admin: 'Platform Assistant',
    student: 'Learning Assistant',
    parent: 'Parent Guide',
  }[role] || 'Assistant';

  return (
    <div className="flex h-full bg-[#F8FAFC]" data-testid="ai-assistant-page">
      {/* Sidebar: Conversation History */}
      <div className="hidden md:flex md:w-72 flex-col bg-white border-r border-slate-100">
        <div className="px-4 py-4 border-b border-slate-100">
          <Button
            onClick={startNewConversation}
            className="w-full bg-[#516EFA] hover:bg-[#4058e8] text-white rounded-xl h-9"
            data-testid="ai-new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Chat
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {loadingConvs ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
            ))
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">No conversations yet</p>
            </div>
          ) : conversations.map(conv => (
            <button
              key={conv.id}
              onClick={() => loadMessages(conv.id)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all group ${
                activeConvId === conv.id
                  ? 'bg-[#eef1ff] text-[#516EFA]'
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
              <span className="text-xs font-medium truncate flex-1">{conv.title}</span>
              <button
                onClick={(e) => handleDelete(conv.id, e)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all flex-shrink-0"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </button>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center">
            <Brain className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <p className="font-outfit font-semibold text-slate-900 text-sm">Eduva AI — {roleLabel}</p>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#14C8C4] animate-pulse" />
              <span className="text-xs text-slate-500">Powered by Claude AI</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={startNewConversation}
            className="ml-auto text-slate-500 hover:text-slate-700 md:hidden"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5">
          {messages.length === 0 && !streamingText ? (
            /* Welcome State */
            <div className="max-w-xl mx-auto text-center pt-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-outfit text-xl font-bold text-slate-900 mb-2">
                How can I help you today?
              </h2>
              <p className="text-slate-500 text-sm mb-8">
                Ask me anything about education, teaching, school management, or get AI-powered assistance for your specific needs.
              </p>
              <div className="grid grid-cols-1 gap-2.5 text-left">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-100 hover:border-[#516EFA]/30 hover:shadow-md transition-all duration-200 text-left group"
                  >
                    <ChevronRight className="w-4 h-4 text-[#516EFA] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    <span className="text-sm text-slate-700">{s}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map(msg => <Message key={msg.id} msg={msg} />)}
              {streamingText && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center flex-shrink-0 mt-1">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <div className="max-w-[75%] bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-800 shadow-sm leading-relaxed">
                    {streamingText}
                    <span className="inline-block w-1.5 h-4 bg-[#516EFA] rounded-sm ml-1 animate-pulse align-middle" />
                  </div>
                </div>
              )}
              {sending && !streamingText && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center flex-shrink-0">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1.5 items-center">
                      {[0, 1, 2].map(i => (
                        <div key={i} className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="bg-white border-t border-slate-100 px-4 py-4 flex-shrink-0">
          <div className="max-w-3xl mx-auto flex gap-3 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Eduva AI anything..."
                rows={1}
                disabled={sending}
                data-testid={AI.chatInput}
                className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#516EFA] focus:ring-2 focus:ring-[#516EFA]/10 transition-all min-h-[44px] max-h-32 disabled:opacity-60"
                style={{ height: 'auto' }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
                }}
              />
            </div>
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || sending}
              data-testid={AI.chatSend}
              className="h-11 w-11 p-0 bg-[#516EFA] hover:bg-[#4058e8] text-white rounded-xl flex-shrink-0 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
